import { FC, useState, useEffect } from 'react';
import { css, Theme, useTheme } from '@emotion/react';
import { Key, PlusCircle, Plus, ArrowSquareOut } from '@phosphor-icons/react';
import type { SSHKeyDocument } from 'common/account/ProfileDocuments';
import { useError } from 'client/lib/Error';
import { usePlatform } from 'client/hooks/usePlatform';
import { Banner } from 'client/components/Banner';
import { Button } from 'client/components/Button';
import { Dialog } from 'client/components/Dialog/Dialog';
import { Input } from 'client/components/Input';
import { borderRadius } from 'client/Theme';
import { validatePublicKey, addSSHKey } from './sshKeyHelpers';

export interface AddSSHKeyDialogProps {
  open: boolean;
  onSave: (sshKeys: SSHKeyDocument[]) => void;
  onClose: () => void;
}

export const AddSSHKeyDialog: FC<AddSSHKeyDialogProps> = ({ open, onSave, onClose }) => {
  const theme = useTheme();
  const { setError } = useError({ showSnackbar: true });
  const platform = usePlatform();
  const [description, setDescription] = useState('');
  const [publicKey, setPublicKey] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const isSSHKeyValid = validatePublicKey(publicKey);

  useEffect(() => {
    if (!open) return;
    setDescription('');
    setPublicKey('');
  }, [open]);

  const handleSave = async () => {
    setError(null);
    setIsLoading(true);
    const response = await addSSHKey({ description, publicKey });
    setIsLoading(false);

    if (response.status === 'invalid ssh key') {
      return setError('Invalid public key. Please check the entered SSH key.', { status: response.status });
    }

    if (response.status !== 'ok') {
      return setError('Something went wrong while saving. Please try again…', { status: (response as any).status });
    }

    onSave(response.sshKeys);
  };

  return (
    <Dialog
      open={open}
      title="Add New SSH Key"
      description="This SSH key can be used to login into deployments"
      icon={<Key color={theme.color.primary[100]} />}
      iconOrnament={<PlusCircle color={theme.color.primary[100]} />}
      actions={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button primary icon={<Plus weight="bold" />} loading={isLoading} onClick={handleSave}>
            Add SSH Key
          </Button>
        </>
      }
      onClose={onClose}
    >
      <div css={helpStyle}>
        <Button href="https://www.ssh.com/academy/ssh-keys" external small icon={<ArrowSquareOut />}>
          What is an SSH key?
        </Button>
        <Button
          href="https://docs.github.com/en/authentication/connecting-to-github-with-ssh/checking-for-existing-ssh-keys"
          external
          small
          icon={<ArrowSquareOut />}
        >
          How to check for existing keys?
        </Button>
        <Button
          href="https://docs.github.com/en/authentication/connecting-to-github-with-ssh/generating-a-new-ssh-key-and-adding-it-to-the-ssh-agent"
          external
          small
          icon={<ArrowSquareOut />}
        >
          How to create an SSH key?
        </Button>
        <Button href="https://stackoverflow.com/a/3828169" external small icon={<ArrowSquareOut />}>
          How to find the public key?
        </Button>
      </div>
      <Input
        label="Description"
        value={description}
        autoFocus
        onChange={(event) => setDescription(event.currentTarget.value)}
        css={inputStyle}
      />
      <Input
        label="Public Key"
        value={publicKey}
        multiline
        rows={10}
        maxRows={12}
        onChange={(event) => setPublicKey(event.currentTarget.value)}
        css={inputStyle}
      />
      <Banner>
        Your public key should start with 'ssh-rsa', 'ecdsa-sha2-nistp256', 'ecdsa-sha2-nistp384', 'ecdsa-sha2-nistp521', 'ssh-ed25519',
        'sk-ecdsa-sha2-nistp256@openssh.com', or 'sk-ssh-ed25519@openssh.com'
      </Banner>
      {platform === 'windows' && (
        <Banner type="warning" style={{ marginTop: 16 }}>
          It looks like you are running Windows. If you are using{' '}
          <a href="https://learn.microsoft.com/windows/wsl/" target="_blank">
            WSL
          </a>{' '}
          as your primary development environment, please note that the typical VS Code install will use{' '}
          <b>SSH keys stored on your Windows user</b> by default.
        </Banner>
      )}
      {!isSSHKeyValid && (
        <Banner type="error" css={errorStyle}>
          The SSH key you entered looks like a private key. Make sure you have pasted the <b>public key</b> before saving. Read more{' '}
          <a target="_blank" href="https://docs.github.com/authentication/connecting-to-github-with-ssh/checking-for-existing-ssh-keys">
            here
          </a>
          .
        </Banner>
      )}
    </Dialog>
  );
};

const helpIconURI = (theme: Theme) =>
  `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='192' height='192' ` +
  `viewBox='0 0 256 256'%3E%3Cpath fill='none' d='M0 0h256v256H0z'/%3E%3Cpath ` +
  `fill='${theme.color.grey[80].replace('#', '%23')}' d='M128 24a104 104 0 1 0 104 104A104 104 0 0 0 128 24Zm-2 48a12 12 ` +
  `0 1 1-12 12 12 12 0 0 1 12-12Zm10 112h-8a8 8 0 0 1-8-8v-48a8 8 0 0 1 0-16h8a8 8 0 0 1 8 8v48a8 8 0 0 1 0 16Z' /%3E%3C/svg%3E")`;

const inputStyle = css`
  margin-bottom: 16px;
`;

const errorStyle = css`
  margin-top: 16px;
`;

const helpStyle = (theme: Theme) => css`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  padding: 8px;
  margin-bottom: 16px;
  border: 2px solid ${theme.color.grey[60]};
  border-radius: ${borderRadius}px;

  .MuiButton-endIcon {
    color: ${theme.color.grey[80]};
  }

  ::before {
    content: '';
    display: block;
    position: absolute;
    top: -8px;
    left: -8px;
    width: 24px;
    height: 24px;
    background-size: 100%;
    background-image: ${helpIconURI(theme)};
    background-color: ${theme.color.background};
  }
`;
